/* eslint-disable consistent-return */
/* eslint-disable react/prop-types */
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { PDFViewer } from "@react-pdf/renderer";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { faArrowUpRightFromSquare, faDownload } from "@fortawesome/pro-regular-svg-icons";
import dayjs from "dayjs";
import Select, { Props } from "react-select";
import { RootState } from "../../../../../reducers";
import { formatCheck2Frontend } from "../../../../../actions/Checks/util";
import { CHECK_LOCATION, VIEW } from "../../../../../actions/Layers/constants";
import { format2Frontend } from "../../../../../actions/Layers/util";
import useConfig from "../../../../../actions/Tenants/config/configHook";
import { CONFIG_TYPES } from "../../../../../actions/Tenants/config/constants";
import { calculateMinMaxAvg } from "../../../../../actions/Tenants/config/utils";
import { breakpoints } from "../../../../../constants/mediaqueries";
import { useRouteLayer } from "../../../../../hooks/useCurrentEntities";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../../utils";
import useAuthorization from "../../../../../utils/authorization";
import { fetchBlob } from "../../../../../utils/base64";
import { callAvosApi } from "../../../../../utils/useAvosApiHook";
import { useRegisterDebugTargets } from "../../../../../utils/useDebugTargets";
import { useTenantDefectDefinitionsState } from "../../../../Forms/useManagedOptionsHook";
import { Button } from "../../../../Helper/Button";
import { Loading } from "../../../../Helper/Loading";
import LoadingProgressBar from "../../../../Helper/LoadingProgressBar";
import FullPageLayout from "../../../Layout/FullPageLayout";
import { intakeReportViewTypes } from "../../../Tenants/Forms/PDFReportConfigForm";
import LayerBreadcrumbs from "../LayerBreadcrumbs";
import { SendReportButton } from "../SendReportButton";
import { setPDFDataForAdvanceRipeningReport, TemplateAdvanceRipeningReport } from "./PDFAdvanceRipeningReport";
import { TemplateGeneralIntakeReport } from "./PDFIntakeReport";
import PdfReader from "./PDFReader";
import QCStatusButton, { QC_STATUS } from "./QCStatusButton";
import { PDFDynamicSectionsReport } from "./Templates/PDFDynamicSectionsReport";
import { prepareChecksForPDF, ReportTemplate } from "./utils";
import { setPDFDataForIntakeReport, setPDFDataForStorageReport } from "./utils/intake";
import { useDynamicSectionsData } from "./utils/useDynamicSectionsData";

export const PDFCountDownText = [
    {
        text: "Loading data...",
        seconds: 3
    },
    {
        text: "Fetching checks...",
        seconds: 3
    },
    {
        text: "List all the fruit...",
        seconds: 3
    },
    {
        text: "Fetching images...",
        seconds: 3
    },
    {
        text: "Rendering PDF...",
        seconds: 4
    },
    {
        text: "Fit images on page...",
        seconds: 4
    },
    {
        text: "Almost there...",
        seconds: 5
    },
    {
        text: "Finishing up...",
        seconds: 3
    }
];

export default function PDFLayerReport() {
    const config = useConfig();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedReportType = useMemo(() => searchParams.get("view") || "", [searchParams]);
    const activeReportViewType = selectedReportType || "full";
    const user = useSelector<RootState>((state) => state.auth.user);
    const layer = useRouteLayer();
    const tenantsIsLoading = useSelector<RootState>((state) => state.tenants.isLoading);
    const [data, setData] = useState<any>();
    const params = useParams();
    const layer_config = config.get_layer_config(layer);
    const {
        definitions: tenantDefectDefinitions,
        isLoaded: tenantDefectDefinitionsLoaded,
    } = useTenantDefectDefinitionsState(layer.fruit_type);

    const report_config_value = params.config_value;
    const report_config = config.get_pdf_report(layer, report_config_value || "");

    // * Resolve the raw config record so we can link to its edit page in AdminBanner
    const tenant_id = (user as any)?.tenant?.tenant_id;
    const pdf_report_raw = config.configs.find(
        (c) => (c.type === CONFIG_TYPES.PDF_REPORT && c.value === report_config_value && c.config?.layer_type === layer.type)
            // fallback: match by value alone when layer_type is absent (some older configs)
            || (c.type === CONFIG_TYPES.PDF_REPORT && c.value === report_config_value && !c.config?.layer_type)
    );
    // const browse_history = useSelector<RootState, Location[]>((state) => state.navigation.browse_history);
    // const lastBrowse = browse_history[browse_history.length - 1];
    // const previousPath = lastBrowse?.pathname || `/layer/${params?.layer_id}`;

    const draft_report_key = `report_eye_${report_config_value}_generated`;
    const draft_report = layer?.[draft_report_key];

    const arrival_check_fields = getFilledArrayOrDefault(layer_config?.show_arrival_check?.fields);

    const check_types = getFilledArrayOrDefault(report_config?.check_types, [report_config?.template === ReportTemplate.advance_ripening ? CHECK_LOCATION.ADVANCE_RIPENING : report_config_value]);
    const check_types_string = check_types.join(",");
    const fallback_check_types = getFilledArrayOrDefault(report_config?.fallback_check_types);
    const fallback_check_types_string = fallback_check_types.join(",");

    // const navigate = useNavigate();
    const { dynamicSectionsData, isLoading: isLoadingDynamicSections } = useDynamicSectionsData();

    const pdf_debug_data = report_config?.template === ReportTemplate.dynamic_sections
        ? { dynamicSectionsData }
        : { data };

    // * Register pdf_report shortcut (direct link to config)
    useRegisterDebugTargets("pdf_report", (tenant_id && pdf_report_raw && report_config) ? [{
        id: `pdf_report_${pdf_report_raw.id}`,
        url: `/tenants/${tenant_id}/config/${pdf_report_raw.id}`,
        text: `${report_config.text} report config`,
    }] : []);

    // * Register pdf report data inspection modal
    useRegisterDebugTargets("pdf_report_data", (tenant_id && pdf_report_raw && report_config) ? [{
        id: `pdf_report_data_${pdf_report_raw.id}`,
        url: "#",
        text: "Report data",
        debug: {
            props: {
                layer,
                ...pdf_debug_data,
            },
            title: `Debug report ${report_config_value}`,
        },
    }] : []);

    // We need a local state here because the pdf rendered cannot reach the react store
    useEffect(() => {
        // * Do not load all this data if the report is already generated or if data is already being fetched in hook
        if (!layer.id || !report_config?.value || !tenantDefectDefinitionsLoaded || layer?.id !== params.layer_id || !check_types_string || draft_report || report_config?.template === ReportTemplate.dynamic_sections) {
            return;
        }

        const fetchData = async () => {
            // fetching children
            try {
                const response = await callAvosApi(`/layers/${layer.id}/children`);
                const children = {
                    ...response.data,
                    results: getFilledArrayOrDefault(response?.data?.results).map(format2Frontend),
                };
                const check_locations_for_report = Array.from(new Set([...check_types, ...fallback_check_types]));

                const checks_response = await callAvosApi(`/layers/${layer.id}/checks`, {
                    params: {
                        with_fruit: true,
                        with_meta: true,
                        check_location: check_locations_for_report.join(","),
                        view_strategy: VIEW.CHILDREN
                    }
                });
                const checks = checks_response.data;

                // * get formatted intake checks, preload images and replace URLs with Blob URLs
                const { checks: formatted_checks, logo_base64 } = await prepareChecksForPDF(checks, config?.root_config?.logo_path);

                let dryMatterMinMaxAvg = {};
                if (report_config?.show_dry_matter) {
                    const lab_check_response = await callAvosApi(`/layers/${layer.id}/checks`, { params: { view_strategy: VIEW.CHILDREN, check_location: CHECK_LOCATION.LAB_CHECK, with_fruit: true } });
                    if (lab_check_response.data) {
                        const dryMatterValues = lab_check_response.data
                            .flatMap((item) => item.avocados)
                            .filter((i) => i.manual_dry_matter)
                            .map((i) => i.manual_dry_matter);
                        dryMatterMinMaxAvg = calculateMinMaxAvg(dryMatterValues, "");
                    }
                }

                let arrival_check = null;
                if (report_config.template === ReportTemplate.intake) {
                    if (isFilledArray(arrival_check_fields)) {
                        const arrival_check_response = await callAvosApi(`/layers/${layer.id}/checks`, { params: { view_strategy: VIEW.CHILDREN, check_location: CHECK_LOCATION.ARRIVAL, with_fruit: true, with_meta: true } });
                        const last_arrival_check = arrival_check_response.data.sort((a, b) => dayjs(b.created).diff(dayjs(a.created)))?.[0];
                        if (last_arrival_check) {
                            arrival_check = formatCheck2Frontend(last_arrival_check);
                        }
                    }
                    const theData = setPDFDataForIntakeReport(config, user, tenantDefectDefinitions, children, formatted_checks, {
                        preferred_check_locations: check_types,
                        fallback_check_locations: fallback_check_types,
                        fallback_check_max_age_days: report_config?.fallback_check_max_age_days,
                    });
                    setData({ ...theData, dryMatterMinMaxAvg, arrival_check, logo_base64, loaded: true });
                }

                if (report_config.template === ReportTemplate.storage) {
                    const theData = setPDFDataForStorageReport(config, user, tenantDefectDefinitions, children, formatted_checks);
                    setData({ ...theData, dryMatterMinMaxAvg, logo_base64, loaded: true });
                }

                if (report_config.template === ReportTemplate.advance_ripening) {
                    const theData = setPDFDataForAdvanceRipeningReport(children, formatted_checks);
                    setData({ ...theData, dryMatterMinMaxAvg, logo_base64, loaded: true });
                }
            } catch (error) {
                toast.error("Could not prepare data for PDF report");
                console.error("Error fetching data:", error); // eslint-disable-line no-console
            }
        };

        fetchData();
    }, [layer.id, tenantDefectDefinitionsLoaded, tenantDefectDefinitions?.length, draft_report, check_types_string, fallback_check_types_string, report_config?.value, arrival_check_fields?.length]);


    // * Setup flag fields
    const business_rules_status = layer?.[report_config?.business_rule_flag_field as string];
    const general_status = layer?.[report_config?.manual_flag_field as string]?.replace("flag-", "") || business_rules_status;

    const [pdfBlob, setPdfBlob] = useState<Blob>();
    const onRender = async ({ blob }) => {
        if (!pdfBlob || pdfBlob.size !== blob.size) {
            setPdfBlob(blob);
        }
    };

    const auth = useAuthorization();
    const canSendReport = report_config?.show_send_email_button || auth.userBelongsToOneOfTeams(report_config?.teams_that_can_send);

    useEffect(() => {
        // * fetch the blob if the draft report is set to send the active draft report
        const fetchAndSetBlob = async () => {
            try {
                const blob = await fetchBlob(draft_report);
                setPdfBlob(blob);
            } catch (error) {
                console.error("Error fetching the blob:", error); // eslint-disable-line no-console
                setPdfBlob(undefined);
            }
        };

        if (draft_report) fetchAndSetBlob();
    }, [draft_report]);

    const blobUrl = useMemo(() => (pdfBlob ? URL.createObjectURL(pdfBlob) : undefined), [pdfBlob]);

    // This fixes the issue with the back button navigation being hijacked by the iframe navigation
    const iframeRef = useRef<HTMLIFrameElement>(null);
    useEffect(() => {
        if (blobUrl && iframeRef.current) {
            iframeRef.current.contentWindow?.location.replace(blobUrl);
        }

        return () => {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [blobUrl]);

    const isDataLoaded = report_config?.template !== ReportTemplate.dynamic_sections ? data?.loaded : !isLoadingDynamicSections;
    const tenantDefectDefinitionsReady = report_config?.template === ReportTemplate.dynamic_sections || tenantDefectDefinitionsLoaded;
    const dependenciesReady: boolean | undefined = layer_config
        && isDataLoaded
        && report_config
        && !tenantsIsLoading
        && tenantDefectDefinitionsReady;

    const [isDelayingRender, setIsDelayingRender] = useState(true);
    const isLoading: boolean = !draft_report && (!dependenciesReady || isDelayingRender);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Only render when everything is finished, with 2.5s margin for setting up internal states
        if (dependenciesReady) {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Wait 2.5s after all dependencies are ready before allowing render
            timeoutRef.current = setTimeout(() => {
                setIsDelayingRender(false);
            }, 2500);
        }

        // Cleanup on unmount or dependency change
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [dependenciesReady]);


    // * leave this here, since it's useful for debugging and this state is not found in redux
    if (report_config?.template === ReportTemplate.dynamic_sections) {
        console.log("DYNAMIC REPORTS DATA", dynamicSectionsData); // eslint-disable-line no-console
    } else {
        console.log("PDF DATA", data); // eslint-disable-line no-console
    }

    const reportViewTypeOptions = useMemo(() => intakeReportViewTypes.filter((option) => report_config?.view_type?.includes(option.value)), [report_config]);
    const qcStatusField = `qc_status_${report_config_value}`;

    const reportContent = () => {
        if (draft_report || isDelayingRender) {
            return null;
        }

        switch (report_config?.template) {
        case ReportTemplate.dynamic_sections:
            return (
                <PDFDynamicSectionsReport
                    onRender={onRender}
                    pdf_config={report_config}
                    layer={layer}
                    dynamicSectionsData={dynamicSectionsData}
                    config={config}
                    viewType={activeReportViewType}
                />
            );

        case ReportTemplate.advance_ripening:
            return (
                <TemplateAdvanceRipeningReport
                    onRender={onRender}
                    pdf_config={report_config}
                    general_status={general_status}
                    layer_config={layer_config}
                    layer={layer}
                    data={data}
                    config={config}
                />
            );

        default:
            return (
                <TemplateGeneralIntakeReport
                    onRender={onRender}
                    pdf_config={report_config}
                    general_status={general_status}
                    layer_config={layer_config}
                    layer={layer}
                    data={data}
                    tenantDefectDefinitions={tenantDefectDefinitions}
                    defect_groups={getFilledArrayOrDefault(report_config?.defect_groups)}
                    config={config}
                    check_types={check_types}
                    viewType={activeReportViewType}
                />
            );
        }
    };

    if (!layer_config || !layer?.label) {
        return <Loading />;
    }

    return (
        <FullPageLayout title={`${layer_config?.text} ${layer.label}`} breadcrumbs={<LayerBreadcrumbs reportTitle={`${report_config_value?.charAt(0)?.toUpperCase()}${report_config_value?.slice(1)} report`} />}>
            <div className="d-flex flex-wrap justify-content-between justify-content-md-end align-items-center mb-3 gap-2" css={css`margin-top: 1px;`}>
                {blobUrl && (
                    <>
                        <div css={css`@media (max-width: ${breakpoints[1]}px) { order: 2; }`}>
                            <Button
                                tag="a"
                                href={draft_report || blobUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                outline
                                color="secondary"
                                icon={faArrowUpRightFromSquare}
                                iconOnlyBreakpoint="md"
                            >
                                Open in New Tab
                            </Button>
                        </div>

                        <div className="me-sm-auto" css={css`@media (max-width: ${breakpoints[1]}px) { order: 2; }`}>
                            <Button
                                tag="a"
                                href={draft_report || blobUrl}
                                download={`${layer.label}-${report_config_value}.pdf`}
                                outline
                                color="primary"
                                icon={faDownload}
                                iconOnlyBreakpoint="md"
                            >
                                Download
                            </Button>
                        </div>
                    </>
                )}

                <div css={css`margin-top: 1px; order: 4; @media (max-width: ${breakpoints[1]}px) { order: 3; }`}>
                    <QCStatusButton
                        pdfConfig={report_config}
                        pdfBlob={pdfBlob}
                        configValue={report_config_value!}
                        draftReportKey={draft_report_key}
                        qcStatusField={qcStatusField}
                    />
                </div>

                {canSendReport && (
                    <div css={css`margin-top: 1px; order: 5; @media (max-width: ${breakpoints[1]}px) { order: 3; }`}>
                        <SendReportButton
                            outline={false}
                            layerId={layer.id}
                            blob={pdfBlob}
                            config_value={report_config_value as string}
                            status={general_status}
                        />
                    </div>
                )}

                {(!!report_config?.view_type?.length && (![QC_STATUS.DRAFT, QC_STATUS.SENT].includes(layer[qcStatusField]))) && (
                    <div className="d-flex pe-lg-2 me-lg-2" css={css`border-right: 1px solid #ccc; order: 3; @media (max-width: ${breakpoints[1]}px) { width: 100%; order: 1; border: none; }`}>
                        <Select
                            css={css`width: 100%; min-width: 280px; @media (min-width: ${breakpoints[1]}px) { width: fit-content; }`}
                            placeholder="Select report view"
                            value={reportViewTypeOptions.find((o) => o.value === activeReportViewType)}
                            options={reportViewTypeOptions as Props["options"]}
                            onChange={(selected: any) => {
                                setPdfBlob(undefined);
                                setSearchParams({ view: selected?.value ?? "" }, {
                                    preventScrollReset: true,
                                    replace: true,
                                });
                            }}
                        />
                    </div>
                )}
            </div>

            {isLoading ? (
                <LoadingProgressBar text={PDFCountDownText.slice(0, 4)} />
            ) : (
                <div className="max-w-100">
                    {!pdfBlob && !draft_report && <LoadingProgressBar text={PDFCountDownText} startIndex={4} />}

                    {!draft_report && !isDelayingRender && (
                        <div css={css`margin: auto; max-width: 100%;`}>
                            <PDFViewer key={`${layer.label}-${activeReportViewType}`} innerRef={iframeRef} style={{ width: "100%", maxWidth: "100%", height: "1200px", opacity: pdfBlob ? 1 : 0 }}>
                                {reportContent() as any}
                            </PDFViewer>
                        </div>
                    )}

                    {draft_report && <PdfReader pdfUrl={draft_report} id={layer.label} />}
                </div>)}
        </FullPageLayout>
    );
}
